import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tenant } from '@up2d8/contracts';
import { SectionHead } from '@up2d8/ui';
import { applyTenantManifest } from '../../offline/manifest';
import { DeskShell } from './desk/desk-shell';
import { useSession } from '../../shared/session';
import { useTenantTheme } from '../../shared/use-tenant-theme';
import { AppBulletins } from './app-bulletins';
import { AppAgendaCard } from './app-agenda-card';
import { AppTeamCard } from './app-team-card';
import { AppFeed } from './app-feed';
import { AppOpenItems } from './app-open-items';
import { AppSupportCard } from './app-support-card';
import { AppFormsCard } from './app-forms-card';
import { AppChecklistsCard } from './app-checklists-page';
import { AppReportCard } from './app-report-page';
import { AppShortcuts } from './app-shortcuts';
import { AppHandoverBanner, AppHandoverWriteCard } from './handover/app-handover-banner';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';
import { OfflineBar } from './offline-bar';
import { PushPrompt } from './push-prompt';
import { GlobalSearch } from '../../shared/global-search';

/**
 * Het startscherm van de medewerker: de tijdlijn.
 *
 * Gebrande kopbalk en onderbalk komen uit `AppShell` — dezelfde schil als de meldingen- en
 * profielpagina, zodat de app als één geheel aanvoelt in plaats van drie losse formulieren
 * (style guide §Mobiele app).
 */
export function AppHomePage() {
  const { t } = useTranslation();
  const session = useSession();
  const tabs = useAppTabs();

  // De huisstijl van de klant op het hoofdscherm van de medewerker (Stap 43).
  useTenantTheme();

  /**
   * De naam van het bedrijf onder het pictogram op het beginscherm (Stap 12).
   *
   * Een pictogram met "UP2D8" eronder zegt een operator niets; "Demo Productie" wel. Het antwoord van
   * deze weg wordt door de service worker bewaard, dus dit werkt ook bij een start zonder netwerk.
   */
  const tenant = useQuery({
    queryKey: ['tenant', 'self'],
    queryFn: async () => {
      const { data } = await session.api.GET('/api/v1/tenant');
      return (data as Tenant | undefined) ?? null;
    },
  });

  // De bedrijfscode en niet de naam: sinds Extra 1 haalt het toestel het manifest bij de server op,
  // en die kent de naam, het pictogram en de kleur al.
  useEffect(() => {
    if (tenant.data?.code) applyTenantManifest({ code: tenant.data.code });
  }, [tenant.data?.code]);

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="home"
      renderLink={renderAppTabLink}
      aside={
        /*
          De rechterkolom (replica-spec §10), en alleen vanaf 1100 px.

          Onder die breedte zet `DeskShell` deze kaarten bóven de tijdlijn — zo staat op een smal
          scherm eerst wat je moet doen en daarna wat er te lezen is. Op een telefoon verdwijnt de
          kolom niet: "Voor jou" is daar het nuttigste blok van het hele scherm.
        */
        <>
          <AppOpenItems />
          <AppTeamCard />
          <AppAgendaCard />
          <AppSupportCard />
        </>
      }
    >
      {/* De mockup heeft geen paginatitel op Home: de zijbalk zegt al waar je bent. Op de telefoon
          is er geen zijbalk, en dan is de titel het enige wat dat zegt. */}
      <h1 className="font-heading text-page-title font-semibold text-text-primary desk:sr-only">
        {t('appHome.title')}
      </h1>

      {/* De overdracht van de vorige ploeg (Stap 39, spec §3 frame 1): het eerste element op Home,
          boven de mededelingen, en alleen tot je "Gelezen" tikt. */}
      <AppHandoverBanner />

      {/* Het zoekveld bovenaan het beginscherm en niet op een eigen tab (Stap 34d). De tabbalk
          heeft er vijf, en zoeken is geen bestemming maar iets wat je doet om érgens te komen —
          een zesde tab zou de vier andere smaller maken voor iets wat je eenmaal per week gebruikt.

          Op desktop staat het in de kopbalk (replica-spec §5.1); twee zoekvelden onder elkaar is
          geen keuze maar een vergissing. */}
      <div className="desk:hidden">
        <GlobalSearch zone="app" />
      </div>

      {/* Verbinding, wachtrij en een nieuwe versie — alleen als er iets te zeggen is. */}
      <OfflineBar />

      {/* Alleen zolang meldingen nog uit staan; sinds UI-2 een smalle balk en geen halve kaart:
          die stond boven de tijdlijn en duwde de reden dat de app bestaat onder de vouw. */}
      <PushPrompt />

      {/* De mededelingen bovenaan (UI-6): een categorie, een periode, en een kruisje. */}
      <AppBulletins />

      <AppShortcuts />

      {/*
        Drie ingangen die geen tab kregen (Stap 36, 37, 38), onder één kop.

        Zonder die kop waren het drie losse witte kaarten tussen de andere witte kaarten, en moest
        je elke lezen om te weten wat het was. Ze verschijnen alleen als de medewerker er iets mee
        kan: geen recht of niets te doen betekent geen kaart.
      */}
      {/*
        De kop verdwijnt mee als er geen enkele kaart overblijft.

        Alle drie de kaarten geven `null` terug zodra er niets te doen is — geen formulier, geen
        soort melding, geen checklist — en dan stond hier een kop met leegte eronder. Dat is precies
        de slordigheid die UI-3 wegnam, en ze was op de testomgeving meteen zichtbaar omdat daar
        nog geen inhoud staat.

        `:not(:has(...))` en niet het omgekeerde, bewust: kent een browser `:has()` niet, dan
        matcht de selector nooit en blijft de sectie gewoon staan. De oude, lelijke toestand dus —
        en niet een sectie die verdwijnt terwijl er wél kaarten in zitten.
      */}
      <section aria-labelledby="actions-heading" className="[&:not(:has([data-app-card]))]:hidden">
        <SectionHead id="actions-heading" title={t('appHome.actions')} />
        <div className="flex flex-col gap-element">
          <AppFormsCard />
          <AppReportCard />
          <AppChecklistsCard />
          <AppHandoverWriteCard />
        </div>
      </section>

      <section aria-labelledby="feed-heading">
        <SectionHead id="feed-heading" title={t('appHome.recent')} />
        <AppFeed />
      </section>
    </DeskShell>
  );
}
